import React, { useState, useEffect, useCallback } from 'react';
import {
    createPost,
    fetchAllPosts,
    fetchImportantPosts,
    fetchYourPosts,
    toggleLikePost,
    addCommentToPost,
    deletePost,
    fetchLocations,
} from '../api';
import { useAuth } from '../context/AuthContext';
import { 
    FiHeart, FiMessageCircle, FiTrash2, FiMoreVertical, 
    FiImage, FiMapPin, FiClock, FiAlertCircle, FiPlus,
    FiSearch, FiX, FiSend, FiPaperclip, FiCheckCircle
} from 'react-icons/fi';

// Helper function to get first name
const getFirstName = (fullName) => {
    if (!fullName) return '';
    const parts = fullName.trim().split(' ');
    return parts.length > 0 ? parts[0] : '';
};

// Time ago helper
const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    
    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + 'y';
    
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + 'mo';
    
    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + 'd';
    
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + 'h';
    
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + 'm';
    
    return Math.floor(seconds) + 's';
};

const NewsfeedPage = () => {
    const { user, isLoading: authLoading } = useAuth();
    const [posts, setPosts] = useState([]);
    const [importantPosts, setImportantPosts] = useState([]);
    const [yourPosts, setYourPosts] = useState([]);
    const [newPostContent, setNewPostContent] = useState('');
    const [newPostLocation, setNewPostLocation] = useState('');
    const [isImportant, setIsImportant] = useState(false);
    const [activeTab, setActiveTab] = useState('all');
    const [commentText, setCommentText] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isCreatePostPopupOpen, setIsCreatePostPopupOpen] = useState(false);
    const [isCommentsPopupOpen, setIsCommentsPopupOpen] = useState(false);
    const [selectedPostId, setSelectedPostId] = useState(null);
    const [allowComments, setAllowComments] = useState(true); // Retaining this, though commented out in JSX
    const [requireConfirmation, setRequireConfirmation] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [locationSearchQuery, setLocationSearchQuery] = useState('');
    const [locations, setLocations] = useState([]);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false); // New state for mobile sidebar
    const [showModal, setShowModal] = useState(false);
const [postToDelete, setPostToDelete] = useState(null);

    const currentUserId = user?.id;
    const currentUserName = user?.firstName && user?.lastName
        ? `${user.firstName} ${user.lastName}`
        : user?.firstName || user?.lastName || '';

    const currentUserFirstName = getFirstName(currentUserName);

    const fetchPosts = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const allPostsRes = await fetchAllPosts();
            setPosts(allPostsRes.data);

            const importantPostsRes = await fetchImportantPosts();
            setImportantPosts(importantPostsRes.data);

            if (currentUserId) {
                const yourPostsRes = await fetchYourPosts(currentUserId);
                setYourPosts(yourPostsRes.data);
            }
        } catch (err) {
            console.error('Error fetching posts:', err);
            setError('Failed to fetch posts. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [currentUserId]);

    useEffect(() => {
        if (!authLoading && user) {
            // console.log("AuthContext User (after load/change):", user);
            fetchPosts();
        } else if (!authLoading && !user) {
            // console.log("AuthContext: No user logged in.");
            fetchPosts(); // Still fetch posts even if user is not logged in, but with limited functionality
        }
    }, [authLoading, user, fetchPosts]);

    useEffect(() => {
        const getLocations = async () => {
            try {
                const response = await fetchLocations();
                setLocations(response.data.data);
            } catch (err) {
                console.error('Error fetching locations:', err);
            }
        };
        // Fetch locations on component mount
        getLocations();
    }, []); // Empty dependency array means this runs once on mount

    const handleCreatePost = async (e) => {
        e.preventDefault();
        if (!newPostContent.trim() || !newPostLocation.trim()) {
            alert('Please enter post content and location.');
            return;
        }
        const authorName = user?.firstName && user?.lastName
            ? `${user.firstName} ${user.lastName}`
            : user?.firstName || user?.lastName || 'Unknown User';

        if (!user || !user.id || !authorName.trim()) {
            setError("User information not available. Please log in to create a post.");
            setIsCreatePostPopupOpen(false);
            return;
        }

        try {
            await createPost({
                author: authorName,
                authorId: user.id,
                authorProfilePicture: user?.profilePicture,
                location: newPostLocation,
                content: newPostContent,
                isImportant,
                allowComments,
                requireConfirmation,
                files: selectedFiles.map(file => file.name),
            });
            setNewPostContent('');
            setNewPostLocation('');
            setIsImportant(false);
            setAllowComments(true);
            setRequireConfirmation(false);
            setSelectedFiles([]);
            setIsCreatePostPopupOpen(false);
            fetchPosts();
        } catch (err) {
            console.error('Error creating post:', err);
            setError('Failed to create post. Please try again.');
        }
    };

    const handleToggleLike = async (postId) => {
        if (!user || !user.id) {
            setError("User not logged in to like posts.");
            // console.log("Attempted to like without currentUserId:", user);
            return;
        }
        const updatePostInArray = (postArray) =>
            postArray.map((post) => {
                if (post._id === postId) {
                    const isLiked = post.likedBy.includes(user.id);
                    return {
                        ...post,
                        likes: isLiked ? post.likes - 1 : post.likes + 1,
                        likedBy: isLiked
                            ? post.likedBy.filter((id) => id !== user.id)
                            : [...post.likedBy, user.id],
                    };
                }
                return post;
            });

        // Optimistically update the UI
        setPosts(prevPosts => updatePostInArray(prevPosts));
        setImportantPosts(prevImportantPosts => updatePostInArray(prevImportantPosts));
        setYourPosts(prevYourPosts => updatePostInArray(prevYourPosts));

        try {
            await toggleLikePost(postId, user.id);
        } catch (err) {
            console.error('Error toggling like:', err);
            // Revert changes on error
            fetchPosts(); // Re-fetch to ensure data consistency
            setError('Failed to toggle like. Please try again.');
        }
    };

    const handleAddComment = async (postId) => {
        const text = commentText[postId];
        if (!text || !text.trim()) {
            alert('Please enter a comment.');
            return;
        }
        const userName = user?.firstName && user?.lastName
            ? `${user.firstName} ${user.lastName}`
            : user?.firstName || user?.lastName || 'Unknown User';

        if (!user || !userName.trim()) {
            setError("User information not available. Cannot add comment.");
            // console.log("Attempted to add comment without user name:", user);
            return;
        }

        const newComment = { user: userName, text, userId: user.id };

        const updateCommentsInPostArray = (postArray, idToUpdate, commentToAdd) =>
            postArray.map((post) => {
                if (post._id === idToUpdate) {
                    const updatedCommentsList = post.commentsList ? [...post.commentsList, commentToAdd] : [commentToAdd];
                    return {
                        ...post,
                        commentsList: updatedCommentsList,
                        comments: (post.comments || 0) + 1
                    };
                }
                return post;
            });

        // Optimistically update all relevant post arrays
        setPosts(prevPosts => updateCommentsInPostArray(prevPosts, postId, newComment));
        setImportantPosts(prevImportantPosts => updateCommentsInPostArray(prevImportantPosts, postId, newComment));
        setYourPosts(prevYourPosts => updateCommentsInPostArray(prevYourPosts, postId, newComment));
        setCommentText((prev) => ({ ...prev, [postId]: '' })); // Clear comment input

        try {
            await addCommentToPost(postId, { userId: user.id, commentText: text });
            // No need to fetchPosts() if optimistic update is reliable and successful
        } catch (err) {
            console.error('Error adding comment:', err);
            setError('Failed to add comment. Please try again.');
            fetchPosts(); // Revert to actual state on error
        }
    };

const handleDeletePost = (postId) => {
    if (!user || !currentUserId) {
        setError("User information not available. Cannot delete post.");
        return;
    }

    setPostToDelete(postId);  
    setShowModal(true);       
};


    const handleViewChange = (view) => {
        setActiveTab(view);
        setIsCommentsPopupOpen(false);
        setSelectedPostId(null);
        setSearchQuery('');
        setLocationSearchQuery('');
        setIsSidebarOpen(false); // Close sidebar after selection on mobile
    };

    const handleFileChange = (e) => {
        setSelectedFiles(Array.from(e.target.files));
    };

    // Filter posts based on search queries
    const filterPosts = (postList) => {
        const mainQuery = searchQuery.toLowerCase();
        const locationQuery = locationSearchQuery.toLowerCase();

        return postList.filter((post) => {
            const matchesMainQuery =
                !mainQuery ||
                post.content.toLowerCase().includes(mainQuery) ||
                post.author.toLowerCase().includes(mainQuery) ||
                post.location.toLowerCase().includes(mainQuery);

            const matchesLocationQuery =
                !locationQuery || post.location.toLowerCase().includes(locationQuery);

            return matchesMainQuery && matchesLocationQuery;
        });
    };
    const confirmDelete = async () => {
    try {
        await deletePost(postToDelete);
        fetchPosts();
    } catch (err) {
        console.error('Error deleting post:', err);
        setError('Failed to delete post. Please try again.');
    } finally {
        setShowModal(false);
        setPostToDelete(null);
    }
};

const cancelDelete = () => {
    setShowModal(false);
    setPostToDelete(null);
};


    const renderPosts = (postList) => (
        <div className="space-y-3">
            {postList.length === 0 && !loading && (
                <div className="bg-white rounded-2xl p-12 text-center border border-gray-100">
                    <div className="w-20 h-20 bg-gray-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                        <FiMessageCircle className="w-10 h-10 text-gray-400" />
                    </div>
                    <p className="text-gray-500 text-lg font-medium">No posts yet</p>
                    <p className="text-gray-400 text-sm mt-1">Be the first to share something!</p>
                </div>
            )}
            {postList.map((post) => (
                <div key={post._id} className="bg-white rounded-2xl border border-gray-100 hover:shadow-lg transition-all duration-200">
                    {/* Post Header */}
                    <div className="flex items-start justify-between p-4 pb-3">
                        <div className="flex items-center space-x-3 flex-1">
                            <div className="relative">
                                <div className="w-12 h-12 rounded-full overflow-hidden ring-2 ring-white shadow-sm">
                                    <img
                                        src={post.authorProfilePicture || 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png'}
                                        alt={`${getFirstName(post.author) || 'User'}'s Profile`}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png';
                                        }}
                                    />
                                </div>
                                {post.isImportant && (
                                    <div className="absolute -top-1 -right-1 bg-red-500 rounded-full p-1">
                                        <FiAlertCircle className="w-3 h-3 text-white" />
                                    </div>
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <p className="font-semibold text-gray-900 text-base truncate">
                                        {post.authorId === user?.id ? currentUserFirstName : getFirstName(post.author)}
                                    </p>
                                    {post.isImportant && (
                                        <span className="px-2 py-0.5 bg-red-50 text-red-600 text-xs font-semibold rounded-full">
                                            Important
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-2 text-sm text-gray-500 mt-0.5">
                                    <FiMapPin className="w-3.5 h-3.5" />
                                    <span className="truncate">{post.location}</span>
                                    <span className="text-gray-300">•</span>
                                    <FiClock className="w-3.5 h-3.5" />
                                    <span>{getTimeAgo(post.date)}</span>
                                </div>
                            </div>
                        </div>
                        {(post.authorId === user?.id || user?.role === 'admin' || user?.role === 'manager') && (
                            <button
                                onClick={() => handleDeletePost(post._id)}
                                className="p-2 hover:bg-red-50 rounded-full transition-colors group"
                                title="Delete post"
                            >
                                <FiTrash2 className="w-4 h-4 text-gray-400 group-hover:text-red-500" />
                            </button>
                        )}
                    </div>

                    {/* Post Content */}
                    <div className="px-4 pb-3">
                        <p className="text-gray-800 text-[15px] leading-relaxed whitespace-pre-wrap">{post.content}</p>
                    </div>

                    {/* Display attached files */}
                    {post.files && post.files.length > 0 && (
                        <div className="px-4 pb-3">
                            <div className="grid grid-cols-2 gap-2">
                                {post.files.map((file, index) => {
                                    const fileExtension = file.split('.').pop().toLowerCase();
                                    const isImage = ['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension);
                                    const isVideo = ['mp4', 'webm', 'ogg'].includes(fileExtension);
                                    const isPdf = fileExtension === 'pdf';
                                    const filePath = `/uploads/${file}`;

                                    return (
                                        <div key={index} className="relative rounded-xl overflow-hidden bg-gray-50">
                                            {isImage && (
                                                <img 
                                                    src={filePath} 
                                                    alt={file} 
                                                    className="w-full h-48 object-cover hover:scale-105 transition-transform duration-200 cursor-pointer" 
                                                    onClick={() => window.open(filePath, '_blank')}
                                                />
                                            )}
                                            {isVideo && (
                                                <video controls className="w-full h-48 object-cover rounded-xl">
                                                    <source src={filePath} type={`video/${fileExtension}`} />
                                                    Your browser does not support the video tag.
                                                </video>
                                            )}
                                            {isPdf && (
                                                <a 
                                                    href={filePath} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="flex flex-col items-center justify-center h-48 hover:bg-gray-100 transition-colors"
                                                >
                                                    <FiPaperclip className="w-8 h-8 text-gray-400 mb-2" />
                                                    <span className="text-sm text-gray-600 px-2 text-center truncate w-full">{file}</span>
                                                </a>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Post Actions */}
                    <div className="border-t border-gray-100 px-4 py-2 flex items-center justify-between">
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => handleToggleLike(post._id)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                                    post.likedBy.includes(user?.id)
                                        ? 'text-red-500 bg-red-50 hover:bg-red-100'
                                        : 'text-gray-600 hover:bg-gray-100'
                                }`}
                            >
                                <FiHeart 
                                    className={`w-5 h-5 ${post.likedBy.includes(user?.id) ? 'fill-current' : ''}`} 
                                />
                                <span className="font-medium text-sm">{post.likes || 0}</span>
                            </button>
                            <button
                                onClick={() => {
                                    setIsCommentsPopupOpen(true);
                                    setSelectedPostId(post._id);
                                }}
                                className="flex items-center gap-2 px-4 py-2 rounded-full text-gray-600 hover:bg-blue-50 hover:text-blue-600 transition-all"
                            >
                                <FiMessageCircle className="w-5 h-5" />
                                <span className="font-medium text-sm">{post.comments || 0}</span>
                            </button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-50">
            {/* Header */}
            <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-200/50">
                <div className="px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                                <FiMessageCircle className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-gray-900">News Feed</h1>
                                <p className="text-xs text-gray-500 hidden sm:block">Stay connected with your team</p>
                            </div>
                        </div>
                        <button
                            onClick={() => setIsCreatePostPopupOpen(true)}
                            className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl font-medium shadow-lg shadow-blue-500/25 transition-all hover:shadow-xl hover:shadow-blue-500/30 hover:scale-105"
                        >
                            <FiPlus className="w-5 h-5" />
                            <span className="hidden sm:inline">New Post</span>
                        </button>
                    </div>
                </div>
            </header>

            {/* Main Container */}
            <div className="px-4 sm:px-6 lg:px-8 py-6">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Sidebar */}
                    <aside className="lg:col-span-3">
                        <div className="sticky top-24 space-y-4">
                            {/* Filters Card */}
                            <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
                                <h2 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide">Filters</h2>
                                <nav className="space-y-1">
                                    <button
                                        onClick={() => handleViewChange('all')}
                                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                                            activeTab === 'all'
                                                ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/20'
                                                : 'text-gray-700 hover:bg-gray-50'
                                        }`}
                                    >
                                        <FiMessageCircle className="w-5 h-5" />
                                        <span className="font-medium">All Posts</span>
                                    </button>
                                    <button
                                        onClick={() => handleViewChange('your')}
                                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                                            activeTab === 'your'
                                                ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/20'
                                                : 'text-gray-700 hover:bg-gray-50'
                                        }`}
                                    >
                                        <FiCheckCircle className="w-5 h-5" />
                                        <span className="font-medium">Your Posts</span>
                                    </button>
                                </nav>
                            </div>

                            {/* Search Card */}
                            <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
                                <h2 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide">Search</h2>
                                <div className="relative">
                                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search posts..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-sm"
                                    />
                                </div>
                            </div>
                        </div>
                    </aside>

                    {/* Main Feed */}
                    <main className="lg:col-span-9">
                        {loading ? (
                            <div className="space-y-3">
                                {Array.from({ length: 3 }).map((_, idx) => (
                                    <div key={idx} className="bg-white rounded-2xl p-6 border border-gray-100 animate-pulse">
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                                            <div className="flex-1">
                                                <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                                                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                                            </div>
                                        </div>
                                        <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                                        <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <>
                                {error && (
                                    <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-4 flex items-center gap-3">
                                        <FiAlertCircle className="w-5 h-5 text-red-600" />
                                        <p className="text-red-800 font-medium">{error}</p>
                                    </div>
                                )}
                                <div className="posts-display-area">
                                    {activeTab === 'all' && renderPosts(filterPosts(posts))}
                                    {activeTab === 'important' && renderPosts(filterPosts(importantPosts))}
                                    {activeTab === 'your' && renderPosts(filterPosts(yourPosts))}
                                </div>
                            </>
                        )}
                    </main>
                </div>
            </div>

            {/* Create Post Modal */}
            {isCreatePostPopupOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn" onClick={() => setIsCreatePostPopupOpen(false)}>
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden animate-slideUp" onClick={(e) => e.stopPropagation()}>
                        {/* Modal Header */}
                        <div className="flex items-center justify-between p-6 border-b border-gray-100">
                            <h2 className="text-xl font-bold text-gray-900">Create Post</h2>
                            <button
                                onClick={() => setIsCreatePostPopupOpen(false)}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <FiX className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                            {/* Location Selector */}
                            <div className="mb-5">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Share With</label>
                                <div className="relative">
                                    <FiMapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <select
                                        value={newPostLocation}
                                        onChange={(e) => setNewPostLocation(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all appearance-none text-gray-700 font-medium"
                                    >
                                        <option value="" disabled>Select Location</option>
                                        {locations.map((location) => (
                                            <option key={location._id} value={location.name}>
                                                {location.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Content Textarea */}
                            <div className="mb-5">
                                <textarea
                                    placeholder="What's on your mind?"
                                    value={newPostContent}
                                    onChange={(e) => setNewPostContent(e.target.value)}
                                    className="w-full h-40 p-4 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all resize-none text-gray-700 placeholder-gray-400"
                                />
                            </div>

                            {/* File Attachment */}
                            <div className="mb-5">
                                <label className="flex items-center gap-2 px-4 py-3 bg-blue-50 hover:bg-blue-100 border-2 border-dashed border-blue-300 rounded-xl cursor-pointer transition-all group">
                                    <FiPaperclip className="w-5 h-5 text-blue-600 group-hover:rotate-12 transition-transform" />
                                    <span className="text-sm font-medium text-blue-600">Attach Images, Videos, or PDFs</span>
                                    <input
                                        type="file"
                                        accept="image/*,video/*,application/pdf"
                                        className="hidden"
                                        multiple
                                        onChange={handleFileChange}
                                    />
                                </label>
                                {selectedFiles.length > 0 && (
                                    <div className="mt-3 space-y-2">
                                        {selectedFiles.map((file, index) => (
                                            <div key={index} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                                                <FiPaperclip className="w-4 h-4 text-gray-400" />
                                                <span className="text-sm text-gray-700 flex-1 truncate">{file.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Options */}
                            <div className="space-y-3 mb-5">
                                <label className="flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors group">
                                    <input
                                        type="checkbox"
                                        checked={requireConfirmation}
                                        onChange={(e) => setRequireConfirmation(e.target.checked)}
                                        className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                                    />
                                    <div>
                                        <span className="text-sm font-medium text-gray-700">Require Confirmation</span>
                                        <p className="text-xs text-gray-500">Readers must confirm they've read this post</p>
                                    </div>
                                </label>
                                <label className="flex items-center gap-3 px-4 py-3 bg-red-50 rounded-xl cursor-pointer hover:bg-red-100 transition-colors group">
                                    <input
                                        type="checkbox"
                                        checked={isImportant}
                                        onChange={(e) => setIsImportant(e.target.checked)}
                                        className="w-5 h-5 text-red-600 rounded focus:ring-2 focus:ring-red-500"
                                    />
                                    <div>
                                        <span className="text-sm font-medium text-gray-700">Mark as Important</span>
                                        <p className="text-xs text-gray-500">This post will be highlighted for all users</p>
                                    </div>
                                </label>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="p-6 border-t border-gray-100 bg-gray-50">
                            <button
                                onClick={handleCreatePost}
                                className="w-full py-3.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl font-semibold shadow-lg shadow-blue-500/25 transition-all hover:shadow-xl hover:shadow-blue-500/30 hover:scale-[1.02]"
                            >
                                Publish Post
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Comments Modal */}
            {isCommentsPopupOpen && selectedPostId && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn" onClick={() => setIsCommentsPopupOpen(false)}>
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden animate-slideUp" onClick={(e) => e.stopPropagation()}>
                        {/* Modal Header */}
                        <div className="flex items-center justify-between p-6 border-b border-gray-100">
                            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                <FiMessageCircle className="w-5 h-5 text-blue-600" />
                                Comments
                            </h2>
                            <button
                                onClick={() => setIsCommentsPopupOpen(false)}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <FiX className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>

                        {/* Comments List */}
                        <div className="p-6 max-h-96 overflow-y-auto">
                            {(() => {
                                const post = posts.concat(importantPosts, yourPosts).find((p) => p._id === selectedPostId);
                                const comments = post?.commentsList;

                                if (!comments || comments.length === 0) {
                                    return (
                                        <div className="text-center py-12">
                                            <div className="w-16 h-16 bg-gray-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                                                <FiMessageCircle className="w-8 h-8 text-gray-400" />
                                            </div>
                                            <p className="text-gray-500 font-medium">No comments yet</p>
                                            <p className="text-gray-400 text-sm mt-1">Be the first to share your thoughts!</p>
                                        </div>
                                    );
                                }

                                return (
                                    <div className="space-y-4">
                                        {comments.map((comment, index) => (
                                            <div key={index} className="flex gap-3 pb-4 border-b border-gray-100 last:border-0">
                                                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold shadow-sm flex-shrink-0">
                                                    {getFirstName(comment.user).charAt(0).toUpperCase()}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-semibold text-gray-900 text-sm mb-1">{getFirstName(comment.user)}</p>
                                                    <p className="text-gray-700 text-sm leading-relaxed">{comment.text}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                );
                            })()}
                        </div>

                        {/* Add Comment Section */}
                        <div className="p-6 border-t border-gray-100 bg-gray-50">
                            <div className="flex items-end gap-3">
                                <div className="flex-1">
                                    <textarea
                                        placeholder="Write a comment..."
                                        value={commentText[selectedPostId] || ''}
                                        onChange={(e) =>
                                            setCommentText((prev) => ({ ...prev, [selectedPostId]: e.target.value }))
                                        }
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                handleAddComment(selectedPostId);
                                            }
                                        }}
                                        className="w-full p-4 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none text-sm"
                                        rows="3"
                                    />
                                </div>
                                <button
                                    onClick={() => handleAddComment(selectedPostId)}
                                    className="p-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl shadow-lg shadow-blue-500/25 transition-all hover:shadow-xl hover:shadow-blue-500/30 hover:scale-105 flex-shrink-0"
                                >
                                    <FiSend className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 animate-slideUp">
                        <div className="w-16 h-16 bg-red-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                            <FiTrash2 className="w-8 h-8 text-red-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">Delete Post?</h2>
                        <p className="text-gray-600 text-center mb-6">This action cannot be undone. Are you sure you want to delete this post?</p>
                        <div className="flex gap-3">
                            <button
                                onClick={cancelDelete}
                                className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="flex-1 px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-xl font-semibold shadow-lg shadow-red-500/25 transition-all hover:shadow-xl hover:shadow-red-500/30"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add custom CSS for animations */}
            <style jsx>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slideUp {
                    from { 
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to { 
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.2s ease-out;
                }
                .animate-slideUp {
                    animation: slideUp 0.3s ease-out;
                }
            `}</style>
        </div>
    );
};

export default NewsfeedPage;




// import React, { useState, useEffect, useCallback } from 'react';
// import {
//     createPost,
//     fetchAllPosts,
//     fetchImportantPosts,
//     fetchYourPosts,
//     toggleLikePost,
//     addCommentToPost,
//     deletePost,
//     fetchLocations,
// } from '../api';
// import { useAuth } from '../context/AuthContext';

// // Helper function to get first name
// const getFirstName = (fullName) => {
//     if (!fullName) return '';
//     const parts = fullName.trim().split(' ');
//     return parts.length > 0 ? parts[0] : '';
// };

// const NewsfeedPage = () => {
//     const { user, isLoading: authLoading } = useAuth();
//     const [posts, setPosts] = useState([]);
//     const [importantPosts, setImportantPosts] = useState([]);
//     const [yourPosts, setYourPosts] = useState([]);
//     const [newPostContent, setNewPostContent] = useState('');
//     const [newPostLocation, setNewPostLocation] = useState('');
//     const [isImportant, setIsImportant] = useState(false);
//     const [activeTab, setActiveTab] = useState('all');
//     const [commentText, setCommentText] = useState({});
//     const [loading, setLoading] = useState(true);
//     const [error, setError] = useState(null);
//     const [isCreatePostPopupOpen, setIsCreatePostPopupOpen] = useState(false);
//     const [isCommentsPopupOpen, setIsCommentsPopupOpen] = useState(false);
//     const [selectedPostId, setSelectedPostId] = useState(null);
//     const [allowComments, setAllowComments] = useState(true); // Retaining this, though commented out in JSX
//     const [requireConfirmation, setRequireConfirmation] = useState(false);
//     const [selectedFiles, setSelectedFiles] = useState([]);
//     const [searchQuery, setSearchQuery] = useState('');
//     const [locationSearchQuery, setLocationSearchQuery] = useState('');
//     const [locations, setLocations] = useState([]);
//     const [isSidebarOpen, setIsSidebarOpen] = useState(false); // New state for mobile sidebar

//     const currentUserId = user?.id;
//     const currentUserName = user?.firstName && user?.lastName
//         ? `${user.firstName} ${user.lastName}`
//         : user?.firstName || user?.lastName || '';

//     const currentUserFirstName = getFirstName(currentUserName);

//     const fetchPosts = useCallback(async () => {
//         setLoading(true);
//         setError(null);
//         try {
//             const allPostsRes = await fetchAllPosts();
//             setPosts(allPostsRes.data);

//             const importantPostsRes = await fetchImportantPosts();
//             setImportantPosts(importantPostsRes.data);

//             if (currentUserId) {
//                 const yourPostsRes = await fetchYourPosts(currentUserId);
//                 setYourPosts(yourPostsRes.data);
//             }
//         } catch (err) {
//             console.error('Error fetching posts:', err);
//             setError('Failed to fetch posts. Please try again.');
//         } finally {
//             setLoading(false);
//         }
//     }, [currentUserId]);

//     useEffect(() => {
//         if (!authLoading && user) {
//             console.log("AuthContext User (after load/change):", user);
//             fetchPosts();
//         } else if (!authLoading && !user) {
//             console.log("AuthContext: No user logged in.");
//             fetchPosts(); // Still fetch posts even if user is not logged in, but with limited functionality
//         }
//     }, [authLoading, user, fetchPosts]);

//     useEffect(() => {
//         const getLocations = async () => {
//             try {
//                 const response = await fetchLocations();
//                 setLocations(response.data.data);
//             } catch (err) {
//                 console.error('Error fetching locations:', err);
//             }
//         };
//         // Fetch locations on component mount
//         getLocations();
//     }, []); // Empty dependency array means this runs once on mount

//     const handleCreatePost = async (e) => {
//         e.preventDefault();
//         if (!newPostContent.trim() || !newPostLocation.trim()) {
//             alert('Please enter post content and location.');
//             return;
//         }
//         const authorName = user?.firstName && user?.lastName
//             ? `${user.firstName} ${user.lastName}`
//             : user?.firstName || user?.lastName || 'Unknown User';

//         if (!user || !user.id || !authorName.trim()) {
//             setError("User information not available. Please log in to create a post.");
//             setIsCreatePostPopupOpen(false);
//             return;
//         }

//         try {
//             await createPost({
//                 author: authorName,
//                 authorId: user.id,
//                 authorProfilePicture: user?.profilePicture,
//                 location: newPostLocation,
//                 content: newPostContent,
//                 isImportant,
//                 allowComments,
//                 requireConfirmation,
//                 files: selectedFiles.map(file => file.name),
//             });
//             setNewPostContent('');
//             setNewPostLocation('');
//             setIsImportant(false);
//             setAllowComments(true);
//             setRequireConfirmation(false);
//             setSelectedFiles([]);
//             setIsCreatePostPopupOpen(false);
//             fetchPosts();
//         } catch (err) {
//             console.error('Error creating post:', err);
//             setError('Failed to create post. Please try again.');
//         }
//     };

//     const handleToggleLike = async (postId) => {
//         if (!user || !user.id) {
//             setError("User not logged in to like posts.");
//             console.log("Attempted to like without currentUserId:", user);
//             return;
//         }
//         const updatePostInArray = (postArray) =>
//             postArray.map((post) => {
//                 if (post._id === postId) {
//                     const isLiked = post.likedBy.includes(user.id);
//                     return {
//                         ...post,
//                         likes: isLiked ? post.likes - 1 : post.likes + 1,
//                         likedBy: isLiked
//                             ? post.likedBy.filter((id) => id !== user.id)
//                             : [...post.likedBy, user.id],
//                     };
//                 }
//                 return post;
//             });

//         // Optimistically update the UI
//         setPosts(prevPosts => updatePostInArray(prevPosts));
//         setImportantPosts(prevImportantPosts => updatePostInArray(prevImportantPosts));
//         setYourPosts(prevYourPosts => updatePostInArray(prevYourPosts));

//         try {
//             await toggleLikePost(postId, user.id);
//         } catch (err) {
//             console.error('Error toggling like:', err);
//             // Revert changes on error
//             fetchPosts(); // Re-fetch to ensure data consistency
//             setError('Failed to toggle like. Please try again.');
//         }
//     };

//     const handleAddComment = async (postId) => {
//         const text = commentText[postId];
//         if (!text || !text.trim()) {
//             alert('Please enter a comment.');
//             return;
//         }
//         const userName = user?.firstName && user?.lastName
//             ? `${user.firstName} ${user.lastName}`
//             : user?.firstName || user?.lastName || 'Unknown User';

//         if (!user || !userName.trim()) {
//             setError("User information not available. Cannot add comment.");
//             console.log("Attempted to add comment without user name:", user);
//             return;
//         }

//         const newComment = { user: userName, text, userId: user.id };

//         const updateCommentsInPostArray = (postArray, idToUpdate, commentToAdd) =>
//             postArray.map((post) => {
//                 if (post._id === idToUpdate) {
//                     const updatedCommentsList = post.commentsList ? [...post.commentsList, commentToAdd] : [commentToAdd];
//                     return {
//                         ...post,
//                         commentsList: updatedCommentsList,
//                         comments: (post.comments || 0) + 1
//                     };
//                 }
//                 return post;
//             });

//         // Optimistically update all relevant post arrays
//         setPosts(prevPosts => updateCommentsInPostArray(prevPosts, postId, newComment));
//         setImportantPosts(prevImportantPosts => updateCommentsInPostArray(prevImportantPosts, postId, newComment));
//         setYourPosts(prevYourPosts => updateCommentsInPostArray(prevYourPosts, postId, newComment));
//         setCommentText((prev) => ({ ...prev, [postId]: '' })); // Clear comment input

//         try {
//             await addCommentToPost(postId, { userId: user.id, commentText: text });
//             // No need to fetchPosts() if optimistic update is reliable and successful
//         } catch (err) {
//             console.error('Error adding comment:', err);
//             setError('Failed to add comment. Please try again.');
//             fetchPosts(); // Revert to actual state on error
//         }
//     };

//     const handleDeletePost = async (postId) => {
//         if (!user || !currentUserId) {
//             setError("User information not available. Cannot delete post.");
//             return;
//         }
//         if (window.confirm('Are you sure you want to delete this post?')) {
//             try {
//                 await deletePost(postId);
//                 fetchPosts();
//             } catch (err) {
//                 console.error('Error deleting post:', err);
//                 setError('Failed to delete post. Please try again.');
//             }
//         }
//     };

//     const handleViewChange = (view) => {
//         setActiveTab(view);
//         setIsCommentsPopupOpen(false);
//         setSelectedPostId(null);
//         setSearchQuery('');
//         setLocationSearchQuery('');
//         setIsSidebarOpen(false); // Close sidebar after selection on mobile
//     };

//     const handleFileChange = (e) => {
//         setSelectedFiles(Array.from(e.target.files));
//     };

//     // Filter posts based on search queries
//     const filterPosts = (postList) => {
//         const mainQuery = searchQuery.toLowerCase();
//         const locationQuery = locationSearchQuery.toLowerCase();

//         return postList.filter((post) => {
//             const matchesMainQuery =
//                 !mainQuery ||
//                 post.content.toLowerCase().includes(mainQuery) ||
//                 post.author.toLowerCase().includes(mainQuery) ||
//                 post.location.toLowerCase().includes(mainQuery);

//             const matchesLocationQuery =
//                 !locationQuery || post.location.toLowerCase().includes(locationQuery);

//             return matchesMainQuery && matchesLocationQuery;
//         });
//     };

//     const renderPosts = (postList) => (
//         <div className="space-y-4">
//             {postList.length === 0 && !loading && (
//                 <p className="text-center text-gray-600 mt-4">No posts to display in this section.</p>
//             )}
//             {postList.map((post) => (
//                 <div key={post._id} className="bg-white p-4 rounded-lg shadow">
//                     <div className="flex items-center space-x-2 mb-2">
//                         <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 bg-gray-200 flex items-center justify-center">
//                             <img
//                                 src={post.authorProfilePicture || 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png'}
//                                 alt={`${getFirstName(post.author) || 'User'}'s Profile Icon`}
//                                 className="w-full h-full object-cover"
//                                 onError={(e) => {
//                                     e.target.onerror = null;
//                                     e.target.src = 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png';
//                                 }}
//                             />
//                         </div>
//                         <div className="flex flex-col">
//                             <p className="font-semibold text-sm">
//                                 {post.authorId === user.id ? (
//                                     <><strong>{currentUserFirstName}</strong></>
//                                 ) : (
//                                     <><strong>{getFirstName(post.author)}</strong></>
//                                 )}
//                                 <span className="text-gray-500 text-xs ml-1">
//                                     from {post.location} - {new Date(post.date).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true })}
//                                 </span>
//                             </p>
//                             {post.isImportant && (
//                                 <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full mt-1 inline-block w-fit">
//                                     Important
//                                 </span>
//                             )}
//                         </div>
//                     </div>
//                     <p className="text-gray-700 text-sm mb-4">{post.content}</p>

//                     {/* Display attached files if any */}
//                     {post.files && post.files.length > 0 && (
//                         <div className="mt-2 mb-4">
//                             <p className="text-xs font-semibold text-gray-600 mb-1">Attachments:</p>
//                             <div className="flex flex-wrap gap-2">
//                                 {post.files.map((file, index) => {
//                                     const fileExtension = file.split('.').pop().toLowerCase();
//                                     const isImage = ['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension);
//                                     const isVideo = ['mp4', 'webm', 'ogg'].includes(fileExtension);
//                                     const isPdf = fileExtension === 'pdf';

//                                     // Placeholder for file paths (adjust as per your backend storage)
//                                     const filePath = `/uploads/${file}`; // Example path, replace with actual

//                                     return (
//                                         <div key={index} className="flex items-center text-blue-600 text-sm">
//                                             {isImage && <img src={filePath} alt={file} className="max-w-24 max-h-24 rounded-md object-cover mr-1" />}
//                                             {isVideo && (
//                                                 <video controls className="max-w-24 max-h-24 rounded-md object-cover mr-1">
//                                                     <source src={filePath} type={`video/${fileExtension}`} />
//                                                     Your browser does not support the video tag.
//                                                 </video>
//                                             )}
//                                             {isPdf && <img src="/pdf-icon.png" alt="PDF" className="w-6 h-6 mr-1" />} {/* Use a generic PDF icon */}
//                                             <a href={filePath} target="_blank" rel="noopener noreferrer" className="hover:underline">
//                                                 {file}
//                                             </a>
//                                         </div>
//                                     );
//                                 })}
//                             </div>
//                         </div>
//                     )}

//                     <div className="mt-2 flex flex-wrap gap-2 text-xs"> {/* Adjusted gap and text size */}
//                         <button
//                             onClick={() => handleToggleLike(post._id)}
//                             className="flex items-center space-x-1 px-2 py-1 border border-gray-300 rounded-lg hover:bg-gray-100"
//                         >
//                             <span className={`text-lg ${post.likedBy.includes(user.id) ? 'text-red-500' : 'text-gray-500'}`}>
//                                 {post.likedBy.includes(user.id) ? '❤️' : '🩶'}
//                             </span>
//                             <span className="text-gray-500">{post.likes}</span>
//                         </button>
//                             <button
//                                 onClick={() => {
//                                     setIsCommentsPopupOpen(true);
//                                     setSelectedPostId(post._id);
//                                 }}
//                                 className="flex items-center space-x-1 px-2 py-1 border border-gray-300 rounded-lg hover:bg-gray-100"
//                             >
//                                 <span className="text-blue-500">
//                                     <img src='/comment.svg' alt="Comment" className="w-4 h-4" />
//                                 </span>
//                                 <span>Comments</span>
//                                 <span className="text-gray-500">{post.comments}</span>
//                             </button>
//                         {(post.authorId === user.id || user.role === 'admin' || user.role === 'manager') && ( // Admins/Managers can also delete
//                             <button
//                                 onClick={() => handleDeletePost(post._id)}
//                                 className="flex items-center space-x-1 px-2 py-1 border border-gray-300 rounded-lg hover:bg-gray-100 text-red-500"
//                             >
//                                 <span>
//                                     <img src='/delete.svg' alt="Delete" className="w-4 h-4" />
//                                 </span>
//                                 <span>Delete</span>
//                             </button>
//                         )}
//                     </div>
//                 </div>
//             ))}
//         </div>
//     );

//     return (
//         <div className="flex flex-col md:flex-row h-screen bg-gray-100">
//             {/* Mobile Header for Sidebar Toggle */}
//             <div className="md:hidden bg-gray-200 p-4 flex justify-between items-center border-b border-gray-300">
//                 <h2 className="text-lg font-semibold">Newsfeed</h2>
//                 <button
//                     onClick={() => setIsSidebarOpen(!isSidebarOpen)}
//                     className="p-2 rounded-md bg-gray-300 hover:bg-gray-400"
//                 >
//                     <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
//                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
//                     </svg>
//                 </button>
//             </div>

//             {/* Sidebar - Conditional rendering for mobile */}
//             <div className={`
//                 ${isSidebarOpen ? 'block' : 'hidden'} 
//                 md:block 
//                 w-full md:w-64 bg-gray-200 p-4 flex-shrink-0 
//                 overflow-y-auto border-b md:border-r border-gray-300
//             `}>
//                 <h2 className="text-lg font-semibold mb-4">ONBOARDING</h2>
//                 <nav className="space-y-2">
//                     <button
//                         onClick={() => handleViewChange('all')}
//                         className={`w-full text-left py-2 px-3 rounded ${activeTab === 'all' ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-300'}`}
//                     >
//                         ALL POSTS
//                     </button>
//                     <button
//                         onClick={() => handleViewChange('important')}
//                         className={`w-full text-left py-2 px-3 rounded ${activeTab === 'important' ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-300'}`}
//                     >
//                         IMPORTANT POSTS
//                     </button>
//                     <button
//                         onClick={() => handleViewChange('your')}
//                         className={`w-full text-left py-2 px-3 rounded ${activeTab === 'your' ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-300'}`}
//                     >
//                         YOUR POSTS
//                     </button>
//                 </nav>

//                 <div className="mt-6 border-t border-[#B1B1B1] pt-[25px]">
//                     <h3 className="text-lg font-semibold mb-2">LOCATIONS</h3>
//                     <input
//                         type="text"
//                         placeholder="Search locations..."
//                         value={locationSearchQuery}
//                         onChange={(e) => setLocationSearchQuery(e.target.value)}
//                         className="w-full p-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
//                     />
//                     <div className="mt-2 space-y-2 max-h-48 overflow-y-auto custom-scrollbar"> {/* Added scrollbar */}
//                         {locations
//                             .filter((location) =>
//                                 location.name.toLowerCase().includes(locationSearchQuery.toLowerCase())
//                             )
//                             .map((location) => (
//                                 <p key={location._id} className="text-gray-600 text-sm">
//                                     {location.name}
//                                 </p>
//                             ))}
//                         {locations.length === 0 && !loading && (
//                             <p className="text-gray-600 text-sm">No locations found.</p>
//                         )}
//                     </div>
//                 </div>
//             </div>

//             {/* Main Content */}
//             <div className="flex-1 p-4 md:p-6 overflow-y-auto"> {/* Adjusted padding */}
//                 <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6"> {/* Adjusted for stacking on mobile */}
//                     <h1 className="text-xl md:text-2xl font-semibold mb-4 md:mb-0"> {/* Larger title on mobile */}
//                         {activeTab === 'all' ? 'NEWS FEED' : activeTab === 'important' ? 'IMPORTANT POSTS' : 'YOUR POSTS'}
//                     </h1>
//                     <div className="flex flex-col sm:flex-row items-stretch sm:items-center w-full md:w-auto space-y-2 sm:space-y-0 sm:space-x-2"> {/* Stack on small screens */}
//                         <input
//                             type="text"
//                             placeholder="Search posts..."
//                             value={searchQuery}
//                             onChange={(e) => setSearchQuery(e.target.value)}
//                             className="w-full sm:w-auto p-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
//                         />
//                         <button
//                             onClick={() => setIsCreatePostPopupOpen(true)}
//                             className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm"
//                         >
//                             CREATE POST
//                         </button>
//                     </div>
//                 </div>

//                 {loading && <p className="text-center text-blue-600 text-xl my-8">Loading posts...</p>}
//                 {error && <p className="text-center text-red-600 text-lg my-8 font-semibold">{error}</p>}

//                 {/* Post Display Area */}
//                 <div className="posts-display-area">
//                     {activeTab === 'all' && renderPosts(filterPosts(posts))}
//                     {activeTab === 'important' && renderPosts(filterPosts(importantPosts))}
//                     {activeTab === 'your' && renderPosts(filterPosts(yourPosts))}
//                 </div>
//             </div>

//             {/* Create Post Popup */}
//             {isCreatePostPopupOpen && (
//                 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"> {/* Added z-50 */}
//                     <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto"> {/* Added max-h and overflow */}
//                         <div className="flex justify-between items-center mb-4">
//                             <h2 className="text-lg font-semibold">Create Post</h2>
//                             <button
//                                 onClick={() => setIsCreatePostPopupOpen(false)}
//                                 className="text-blue-500 text-xl"
//                             >
//                                 ✕
//                             </button>
//                         </div>
//                         <div className="mb-4">
//                             <label className="block text-sm font-medium text-gray-700 mb-1">Share With</label>
//                             <select
//                                 value={newPostLocation}
//                                 onChange={(e) => setNewPostLocation(e.target.value)}
//                                 className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
//                             >
//                                 <option value="" disabled>Select Location</option>
//                                 {locations.map((location) => (
//                                     <option key={location._id} value={location.name}>
//                                         {location.name}
//                                     </option>
//                                 ))}
//                             </select>
//                         </div>
//                         <div className="mb-4">
//                             <textarea
//                                 placeholder="What's Happening?"
//                                 value={newPostContent}
//                                 onChange={(e) => setNewPostContent(e.target.value)}
//                                 className="w-full h-32 p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
//                             />
//                         </div>
//                         <div className="mb-4">
//                             <label className="flex items-center space-x-1 cursor-pointer text-blue-500 text-sm">
//                                 <span>&#128279;</span>
//                                 <span>Attach Images, Videos, PDFs</span>
//                                 <input
//                                     type="file"
//                                     accept="image/*,video/*,application/pdf"
//                                     className="hidden"
//                                     multiple
//                                     onChange={handleFileChange}
//                                 />
//                             </label>
//                             {selectedFiles.length > 0 && (
//                                 <ul className="mt-2 text-sm text-gray-600">
//                                     {selectedFiles.map((file, index) => (
//                                         <li key={index}>{file.name}</li>
//                                     ))}
//                                 </ul>
//                             )}
//                         </div>
//                         <div className="flex flex-col space-y-2 mb-4 text-sm"> {/* Stack checkboxes on mobile */}
//                             <label className="flex items-start space-x-1">
//                                 <input
//                                     type="checkbox"
//                                     checked={requireConfirmation}
//                                     onChange={(e) => setRequireConfirmation(e.target.checked)}
//                                     className="form-checkbox mt-[2px]"
//                                 />
//                                 <span>Require Confirmation</span>
//                             </label>
//                             {/* <label className="flex items-start space-x-1">
//                                 <input
//                                     type="checkbox"
//                                     checked={allowComments}
//                                     onChange={(e) => setAllowComments(e.target.checked)}
//                                     className="form-checkbox mt-[2px]"
//                                 />
//                                 <span>Allow Comments</span>
//                             </label> */}
//                             <label className="flex items-start space-x-1">
//                                 <input
//                                     type="checkbox"
//                                     checked={isImportant}
//                                     onChange={(e) => setIsImportant(e.target.checked)}
//                                     className="form-checkbox mt-[2px]"
//                                 />
//                                 <span>Mark as Important</span>
//                             </label>
//                         </div>
//                         <button
//                             onClick={handleCreatePost}
//                             className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 text-sm"
//                         >
//                             Post
//                         </button>
//                     </div>
//                 </div>
//             )}

//             {/* Comments Popup */}
//             {isCommentsPopupOpen && selectedPostId && (
//                 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"> {/* Added z-50 */}
//                     <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto"> {/* Added max-h and overflow */}
//                         <div className="flex justify-between items-center mb-4">
//                             <h2 className="text-lg font-semibold">Comments</h2>
//                             <button
//                                 onClick={() => setIsCommentsPopupOpen(false)}
//                                 className="text-blue-500 text-xl"
//                             >
//                                 ✕
//                             </button>
//                         </div>
//                         <div className="space-y-4 max-h-60 overflow-y-auto custom-scrollbar pr-2"> 
//                             {(() => {
//                                 const post = posts.concat(importantPosts, yourPosts).find((p) => p._id === selectedPostId);
//                                 const comments = post?.commentsList;

//                                 if (!comments || comments.length === 0) {
//                                     return <p className="text-gray-500 text-sm">No comments yet.</p>;
//                                 }

//                                 return comments.map((comment, index) => (
//                                     <div key={index} className="border-b border-gray-200 pb-2 text-sm">
//                                         <p className="font-semibold">{getFirstName(comment.user)}</p>
//                                         <p className="text-gray-700">{comment.text}</p>
//                                     </div>
//                                 ));
//                             })()}
//                         </div>
//                         {/* Only allow adding comments if the post explicitly allows it */}

//                             <div className="mt-4 flex items-center space-x-2">
//                                 <input
//                                     type="text"
//                                     placeholder="Enter Your Comment Here"
//                                     value={commentText[selectedPostId] || ''}
//                                     onChange={(e) =>
//                                         setCommentText((prev) => ({ ...prev, [selectedPostId]: e.target.value }))
//                                     }
//                                     className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
//                                 />
//                                 <button
//                                     onClick={() => handleAddComment(selectedPostId)}
//                                     className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm flex-shrink-0"
//                                 >
//                                     SEND
//                                 </button>
//                             </div>
                       
//                     </div>
//                 </div>
//             )}
//         </div>
//     );
// };

// export default NewsfeedPage;